import { faCircle } from '@fortawesome//pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Grid,
  lighten,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@material-ui/core';
import { sumBy } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import { PieChart } from '../../../../../components/muiLegacy/Charts/PieChart';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { VolumeIntervalGq } from '../../../../../graphql/projects/queries/analysis/useGetSearchVolumeDistributionGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { formatPercent } from '../../../../../logic/otherUtilities/formatting/formatPercent';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';

export interface SearchVolumeDistributionPieChartProps extends ChartProps { }

const SearchVolumeDistributionPieChart: React.FC<SearchVolumeDistributionPieChartProps> = ({
  project,
  setAnalysisFilterState,
  analysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTagGroupId: true,
          showTagId: true,
        },
      })),
    []
  );

  // Fetch data
  const intervals: VolumeIntervalGq[] = [
    {
      startVolume: 0,
      endVolume: 10,
      name: `${internationalText.chart.search_volume_distribution.interval_1[language]} (0 - 10)`,
    },
    {
      startVolume: 11,
      endVolume: 100,
      name: `${internationalText.chart.search_volume_distribution.interval_2[language]} (11 - 100)`,
    },
    {
      startVolume: 101,
      endVolume: 1_000,
      name: `${internationalText.chart.search_volume_distribution.interval_3[language]} (101 - 1,000)`,
    },
    {
      startVolume: 1_001,
      name: `${internationalText.chart.search_volume_distribution.interval_4[language]} (> 1,001)`,
    },
  ];

  const colorScheme = [...palette.primaryScale, palette.secondaryScale[1]];

  const { result, query } = projectsApiService.queries.analysis.useGetSearchVolumeDistribution({
    projectId: project.id,
    volumeIntervals: intervals,
    serpFilter: analysisFilterState?.serpOnly,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const totalVolume = sumBy(result?.searchVolumeAnalyses, (x) => x?.searchVolume) ?? 0;

  // Render
  if (didQueryTimeout(query)) {
    return <DataTimedOut />;
  }

  if (query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (totalVolume <= 0) {
    return <NotEnoughData />;
  }

  const data = result?.searchVolumeAnalyses?.map((result, index) => ({
    id: result.name,
    value: result.searchVolume,
  }));

  return (
    <div>
      <PieChart
        data={data}
        valueForm={' ^-.2~s'}
        customColorScheme={colorScheme}
        palette={palette}
      />
      <Grid container justifyContent="center">
        <Grid item>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {internationalText.chart.search_volume_distribution.label_vol_interval[language]}
                </TableCell>
                <TableCell>
                  {internationalText.chart.search_volume_distribution.label_vol_sum[language]}
                </TableCell>
                <TableCell>
                  {internationalText.chart.search_volume_distribution.label_key_count[language]}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.searchVolumeAnalyses.map((searchVolumeAnalysis, index) => (
                <TableRow key={(searchVolumeAnalysis.name, index)}>
                  <TableCell>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <FontAwesomeIcon icon={faCircle} color={colorScheme[index]} />
                      </Grid>
                      <Grid item>{searchVolumeAnalysis.name}</Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>
                    {formatNumber(searchVolumeAnalysis.searchVolume)} (
                    {formatPercent(searchVolumeAnalysis.searchVolume / totalVolume)}%)
                  </TableCell>
                  <TableCell>{formatNumber(searchVolumeAnalysis.keywordCount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </div>
  );
};

export default SearchVolumeDistributionPieChart;
